'use client';

import { useInvoicesQuery } from '../api/billingApi';
import type { Invoice } from '../types/billing.types';

export function InvoiceList() {
  const { data, isLoading } = useInvoicesQuery();
  if (isLoading) {
    return (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        Loading invoices…
      </div>
    );
  }
  const rows = data ?? [];
  return (
    <div className="rounded-lg border border-border">
      <div className="border-b border-border px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">
        Invoices
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          No invoices yet. They&apos;ll show up here after your first payment.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row) => (
            <InvoiceRow key={row.id} invoice={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const paid = invoice.paid_at
    ? new Date(invoice.paid_at).toLocaleDateString()
    : invoice.created_at
    ? new Date(invoice.created_at).toLocaleDateString()
    : '';
  return (
    <li className="flex items-center justify-between px-4 py-3 text-sm">
      <div>
        <div className="font-medium">{invoice.label}</div>
        <div className="text-xs text-muted-foreground">
          {paid} · {invoice.status}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono">${invoice.amount_usd}</span>
        <ReceiptLink invoice={invoice} />
      </div>
    </li>
  );
}

function ReceiptLink({ invoice }: { invoice: Invoice }) {
  if (invoice.provider === 'stripe' && invoice.invoice_pdf_url) {
    return (
      <a
        href={invoice.invoice_pdf_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-coral underline"
      >
        PDF
      </a>
    );
  }
  if (invoice.provider === 'nowpayments' && invoice.block_explorer_url) {
    return (
      <a
        href={invoice.block_explorer_url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-coral underline"
        title={invoice.tx_hash ?? undefined}
      >
        Explorer
      </a>
    );
  }
  if (invoice.tx_hash) {
    // Chain ambiguous — show the hash, no link.
    return (
      <span
        className="font-mono text-xs text-muted-foreground"
        title={invoice.tx_hash}
      >
        {invoice.tx_hash.slice(0, 10)}…
      </span>
    );
  }
  return null;
}
